import { useEffect, useState } from "react";
import { Download, Loader2, ArrowRight, Copy, Check, FileEdit } from "lucide-react";
import { API_BASE } from "@/lib/api";
import ProcessingProgress from "@/components/ProcessingProgress";
import { ALIGN_STAGES } from "@/lib/processing-stages";
import CareerMilestoneBanner from "@/components/CareerMilestoneBanner";
import {
  buildResumeOptimizedMilestone,
  buildATSReadyMilestone,
} from "@/lib/milestones";
import ScoreImprovementPanel from "./ScoreImprovementPanel";
import ContextualFeedback from "@/components/ContextualFeedback";
import DeliverableFeedback from "@/components/DeliverableFeedback";
import TemplateGallery from "./TemplateGallery";
import { track } from "@/lib/analytics";
import { fmtDate } from "./utils";

/**
 * AlignmentResults - the "Aligned resume is ready" panel. Extracted from
 * `pages/ResumeAlignment.jsx` so the container page only has to render
 * `<AlignmentResults aligned={aligned} ... />` and stay focused on
 * orchestration (analyze → align → poll → milestones) rather than
 * layout.
 *
 * Contract (all props preserved verbatim from the old inline JSX):
 *   aligned                - full aligned-resume record from the API
 *   alignedHistory         - historical alignments for "Compare vs" picker
 *   compareTargetId        - currently selected compare option
 *   setCompareTargetId     - setter
 *   compareTarget          - resolved compare record (or null)
 *   alignStage             - current ProcessingProgress stage id
 *   alignStartedAt         - timestamp we started the align
 *   onCancelAlign          - cancel-in-flight handler
 *   copyAlignedText        - copy handler
 *   copiedAligned          - clipboard "just copied" flag
 *   setCoverLetterOpen     - opens the CoverLetterModal
 */
export default function AlignmentResults({
  aligned,
  alignedHistory,
  compareTargetId,
  setCompareTargetId,
  compareTarget,
  alignStage,
  alignStartedAt,
  onCancelAlign,
  copyAlignedText,
  copiedAligned,
  setCoverLetterOpen,
}) {
  const [selectedTemplate, setSelectedTemplate] = useState(aligned?.template_id || "");
  useEffect(() => {
    setSelectedTemplate(aligned?.template_id || "");
  }, [aligned?.aligned_id, aligned?.template_id]);
  const tplQS = selectedTemplate ? `&template_id=${selectedTemplate}` : "";
  if (!aligned) return null;
  return (
    <>
      {/* Career Milestone banners, celebrate resume optimisation wins */}
      <CareerMilestoneBanner milestone={buildResumeOptimizedMilestone(aligned)} />
      <CareerMilestoneBanner milestone={buildATSReadyMilestone(aligned)} />

      <div
        data-testid="aligned-result"
        className="p-5 rounded-xl border border-emerald-500/20 bg-emerald-500/5 space-y-4 animate-in"
      >
        {aligned.align_status === "processing" && (
          <ProcessingProgress
            testId="align-progress"
            title="Rewriting your resume for this role"
            stages={ALIGN_STAGES}
            currentStage={alignStage}
            status="processing"
            startedAt={alignStartedAt}
            onCancel={onCancelAlign}
          />
        )}
        {aligned.align_status === "failed" && (
          <div
            data-testid="aligned-failed"
            className="rounded-lg border border-rose-500/25 bg-rose-500/[0.06] px-3 py-2 text-sm text-rose-100"
          >
            {aligned.error_message || "AI alignment is temporarily unavailable. Please retry in a moment."}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">
              {aligned.align_status === "processing"
                ? "Aligned resume drafting…"
                : aligned.align_status === "failed"
                ? "Alignment failed"
                : "Aligned resume ready"}
            </div>
            <div className="text-sm text-zinc-100 font-medium mt-1">
              {aligned.align_status === "processing"
                ? "The rewrite will appear below in ~10 seconds."
                : "Version comparison + download below"}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {alignedHistory.filter((h) => h.aligned_id !== aligned.aligned_id).length > 0 && (
              <label className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-400">
                <span className="text-zinc-500">Compare vs</span>
                <select
                  data-testid="aligned-compare-select"
                  value={compareTargetId}
                  onChange={(e) => setCompareTargetId(e.target.value)}
                  className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-emerald-500/50 hover:border-zinc-700 transition-colors normal-case tracking-normal"
                >
                  <option value="">Original</option>
                  {alignedHistory
                    .filter((h) => h.aligned_id !== aligned.aligned_id)
                    .map((h) => {
                      const label = [h.job_title, h.company].filter(Boolean).join(" @ ") || "Aligned";
                      return (
                        <option key={h.aligned_id} value={h.aligned_id}>
                          {label} · {fmtDate(h.created_at)}
                        </option>
                      );
                    })}
                </select>
              </label>
            )}
            <button
              type="button"
              onClick={copyAlignedText}
              data-testid="copy-aligned-btn"
              disabled={!aligned?.aligned_resume}
              className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-lg px-3 py-2 text-xs disabled:opacity-60 transition-colors"
              title="Copy aligned resume to clipboard"
            >
              {copiedAligned ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
              {copiedAligned ? "Copied" : "Copy"}
            </button>
            <a
              data-testid="download-aligned-pdf-link"
              href={`${API_BASE}/resume/aligned/${aligned.aligned_id}/download?format=pdf${tplQS}`}
              onClick={() => track("templates.download", { format: "pdf", template_id: selectedTemplate || "default", aligned_id: aligned.aligned_id })}
              className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-lg px-3 py-2 text-xs transition-colors"
              title="Download as PDF (recommended for hiring managers)"
            >
              <Download className="h-3.5 w-3.5" /> PDF
            </a>
            <a
              data-testid="download-aligned-link"
              href={`${API_BASE}/resume/aligned/${aligned.aligned_id}/download?format=docx${tplQS}`}
              onClick={() => track("templates.download", { format: "docx", template_id: selectedTemplate || "default", aligned_id: aligned.aligned_id })}
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold rounded-lg px-3.5 py-2 text-sm"
            >
              <Download className="h-3.5 w-3.5" /> Download .docx
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                {compareTargetId && compareTarget
                  ? [compareTarget.job_title, compareTarget.company].filter(Boolean).join(" @ ") || "Previous alignment"
                  : "Original"}
              </div>
              {compareTargetId && compareTarget && (
                <a
                  data-testid="compare-target-download"
                  href={`${API_BASE}/resume/aligned/${compareTarget.aligned_id}/download?format=docx`}
                  className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-emerald-400 inline-flex items-center gap-1 transition-colors"
                  title="Download the compared version (.docx)"
                >
                  <Download className="h-3 w-3" />
                </a>
              )}
            </div>
            <pre
              data-testid="aligned-compare-left"
              className="text-[11px] whitespace-pre-wrap rounded-lg p-3 max-h-64 overflow-y-auto font-mono border bg-zinc-950/60 text-zinc-400 border-zinc-800"
            >
              {compareTargetId
                ? (compareTarget ? compareTarget.aligned_resume : "Loading previous version…")
                : aligned.original_resume}
            </pre>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">
              {compareTargetId ? "Current (this alignment)" : "Aligned"}
            </div>
            <pre
              data-testid="aligned-resume-text"
              className="text-[11px] text-emerald-100 whitespace-pre-wrap bg-emerald-500/5 rounded-lg p-3 max-h-64 overflow-y-auto font-mono border border-emerald-500/20"
            >
              {aligned.aligned_resume}
            </pre>
          </div>
        </div>

        {aligned.change_notes?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              What changed
            </div>
            <ul className="text-xs text-zinc-300 space-y-1.5">
              {aligned.change_notes.map((c, i) => (
                <li key={i} className="flex gap-2"><span className="text-emerald-400">▸</span>{c}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Score Improvement, Before vs After. Shown as a
            "Rescoring…" state while the background rescore is
            running, then replaces itself with real numbers when
            the poll picks up rescore_status='ready'. */}
        {aligned.rescore_status === "pending" ? (
          <div
            data-testid="score-improvement-pending"
            className="rounded-xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.05] via-zinc-900/40 to-zinc-950/60 p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Loader2 className="h-4 w-4 text-emerald-400 animate-spin" />
              <div className="text-[10px] uppercase tracking-widest text-emerald-400">
                Scoring your optimized resume…
              </div>
            </div>
            <div className="text-xs text-zinc-400">
              Your Before vs After comparison lands in a few seconds. We&apos;re running the same rubric
              that scored the original so the delta is a fair, like-for-like measurement.
            </div>
          </div>
        ) : aligned.rescore_status === "error" ? (
          <div className="rounded-xl border border-rose-500/25 bg-rose-500/[0.05] p-3 text-xs text-rose-200">
            Couldn&apos;t compute the Before vs After scores. Your aligned resume is ready above.
            Re-run alignment if you&apos;d like the comparison.
          </div>
        ) : aligned.rescore_status === "cancelled" ? (
          <div
            data-testid="score-improvement-cancelled"
            className="rounded-xl border border-zinc-700/60 bg-zinc-900/40 p-3 text-xs text-zinc-400"
          >
            Scoring was skipped because you re-ran AI Align on this analysis. Open the newest
            aligned resume from the history list to see its Before vs After comparison.
          </div>
        ) : (
          <ScoreImprovementPanel
            original={aligned.original_scores}
            updated={aligned.updated_scores}
            updatedAnalysis={aligned.updated_analysis}
          />
        )}

        {/* Premium template gallery, presentation layer for the aligned content */}
        {aligned.aligned_resume && aligned.align_status !== "processing" && aligned.align_status !== "failed" && (
          <TemplateGallery
            alignedId={aligned.aligned_id}
            initialTemplateId={aligned.template_id}
            onTemplateSelected={setSelectedTemplate}
          />
        )}

        {/* Next-step CTA, pairs the aligned resume with a matching cover letter */}
        <button
          type="button"
          data-testid="draft-cover-letter-btn"
          onClick={() => setCoverLetterOpen(true)}
          className="group w-full rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/[0.08] via-amber-500/[0.04] to-transparent hover:from-amber-500/[0.14] hover:border-amber-500/50 px-4 py-3 flex items-center justify-between gap-3 text-left transition-colors"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="h-9 w-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
              <FileEdit className="h-4 w-4 text-amber-300" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-zinc-100">
                Create My Cover Letter
              </div>
              <div className="text-xs text-zinc-400 leading-relaxed">
                Uses your aligned resume + this job description. Three tones, editable, ready to paste.
              </div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-300 group-hover:text-amber-200 whitespace-nowrap">
            Draft <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </button>

        {/* One-question contextual feedback after the alignment report */}
        <ContextualFeedback
          type="contextual_report_helpful"
          entityKey={aligned.aligned_id}
          question="Did this report help you prepare?"
          options={[
            { label: "Yes", value: "yes" },
            { label: "Somewhat", value: "somewhat" },
            { label: "No", value: "no" },
          ]}
          context={{ aligned_id: aligned.aligned_id }}
        />

        <DeliverableFeedback
          deliverable="resume_alignment"
          label="Resume Alignment"
          entityKey={aligned.aligned_id}
          context={{ aligned_id: aligned.aligned_id }}
        />
      </div>
    </>
  );
}
